import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, map, merge, Subscription, throttleTime } from 'rxjs';
import { Nullable } from '../../common/domain/types/types';
import { PresenceModalResult, PresenceModalService } from '../infrastructure/presence-modal.service';
import { MasterDataService } from '../../master-data/domain/master-data-service';
import { EventsService } from '../../events/events.service';
import { UserLoggedOutEvent } from '../../events/user-logged-out.event';
import { UserPresenceConfiguration } from '../../master-data/domain/master-data-configuration';
import { InitializerService } from '../../common/domain/initializer/initializer.service';

@Injectable({
  providedIn: 'root',
})
export class UserPresenceService implements OnDestroy {
  private isStarted = false;
  private isEnabled = false;
  private isWarningOpen = false;
  private isLogoutInProgress = false;
  private configurationVersion = 0;
  private activitySubscription: Nullable<Subscription> = null;
  private configSubscription: Nullable<Subscription> = null;
  private inactivityTimeoutId: number | null = null;
  private currentConfig: UserPresenceConfiguration;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone,
    private readonly masterDataService: MasterDataService,
    private readonly presenceModalService: PresenceModalService,
    private readonly eventsService: EventsService
  ) {}

  public ngOnDestroy(): void {
    this.stop();
  }

  public async start(): Promise<void> {
    await InitializerService.waitFor(this.masterDataService);

    if (this.isStarted) {
      return;
    }

    this.currentConfig = this.masterDataService.getUserPresenceConfiguration();
    this.isStarted = true;
    this.isLogoutInProgress = false;

    this.subscribeToConfigChanges();
    this.registerActivityListeners();
    this.updatePresenceConfiguration(this.currentConfig);
  }

  public stop(): void {
    console.log('Stopping user presence service');
    this.configurationVersion += 1;
    this.isStarted = false;
    this.isEnabled = false;
    this.isWarningOpen = false;
    this.isLogoutInProgress = false;

    void this.presenceModalService.closeWarningModal();
    this.clearInactivityTimer();
    this.unsubscribeActivityListeners();
    this.unsubscribeConfigChanges();
  }

  public notifyActivity(): void {
    if (!this.isStarted) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    console.log(' ---> Activity detected, resetting inactivity timer');
    this.scheduleInactivityTimer();
  }

  public restart(): void {
    if (!this.isStarted) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.scheduleInactivityTimer();
  }

  private subscribeToConfigChanges(): void {
    this.unsubscribeConfigChanges();

    this.configSubscription = this.masterDataService.userPresenceConfiguration$.subscribe(
      (config: UserPresenceConfiguration) => {
        if (!this.isStarted) {
          return;
        }

        this.updatePresenceConfiguration(config);
      }
    );
  }

  private updatePresenceConfiguration(configuration: Nullable<UserPresenceConfiguration>): void {
    this.configurationVersion += 1;
    this.currentConfig = configuration;
    this.isEnabled = this.currentConfig?.enabled ?? false;
    this.isWarningOpen = false;

    void this.presenceModalService.closeWarningModal();
    this.clearInactivityTimer();

    if (!this.isEnabled) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.scheduleInactivityTimer();

    console.log(`Starting user presence service:`);
    console.log(` -> opens after ${configuration.delay_seconds}s of inactivity`);
    console.log(` -> gives you ${configuration.response_timeout_seconds}s to respond`);
  }

  private registerActivityListeners(): void {
    this.unsubscribeActivityListeners();

    this.ngZone.runOutsideAngular((): void => {
      // using passive listeners, to avoid penalizing scroll
      const click$ = fromEvent(this.document, 'click').pipe(map((event) => ({ type: 'click', event })));

      const touchStart$ = fromEvent(this.document, 'touchstart', { passive: true }).pipe(
        map((event) => ({ type: 'touchstart', event }))
      );

      const keyDown$ = fromEvent(this.document, 'keydown').pipe(map((event) => ({ type: 'keydown', event })));

      // using throttle to capture only one event within the specified time interval (for events that are triggered very frequently)
      const touchMove$ = fromEvent(this.document, 'touchmove', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'touchmove', event }))
      );
      const wheel$ = fromEvent(this.document, 'wheel', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'wheel', event }))
      );

      const scroll$ = fromEvent(window, 'scroll', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'scroll', event }))
      );

      this.activitySubscription = merge(click$, touchStart$, touchMove$, keyDown$, wheel$, scroll$).subscribe(
        ({ type }): void => {
          // console.log('[UserPresence] Activity from:', type);
          this.notifyActivity();
        }
      );
    });
  }

  private unsubscribeActivityListeners(): void {
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = null;
  }

  private unsubscribeConfigChanges(): void {
    this.configSubscription?.unsubscribe();
    this.configSubscription = null;
  }

  private scheduleInactivityTimer(): void {
    this.clearInactivityTimer();

    if (!this.isEnabled) {
      return;
    }

    if (!this.currentConfig) {
      return;
    }

    if (this.currentConfig.delay_seconds === null) {
      return;
    }

    const configurationVersion = this.configurationVersion;
    this.inactivityTimeoutId = window.setTimeout((): void => {
      void this.handleInactivityTimeout(configurationVersion);
    }, this.currentConfig.delay_seconds * 1000);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimeoutId !== null) {
      window.clearTimeout(this.inactivityTimeoutId);
      this.inactivityTimeoutId = null;
    }
  }

  private async handleInactivityTimeout(configurationVersion: number): Promise<void> {
    if (!this.isStarted) {
      return;
    }

    if (!this.isCurrentConfigurationVersion(configurationVersion)) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.isWarningOpen = true;

    try {
      const result: PresenceModalResult = await this.presenceModalService.openWarningModal(
        this.currentConfig.response_timeout_seconds
      );

      if (!this.isStarted) {
        return;
      }

      if (!this.isCurrentConfigurationVersion(configurationVersion)) {
        return;
      }

      if (!this.isEnabled) {
        return;
      }

      if (result === 'confirmed') {
        this.isWarningOpen = false;
        this.scheduleInactivityTimer();
        return;
      }

      await this.executeLogout(configurationVersion);
    } finally {
      this.isWarningOpen = false;
    }
  }

  private async executeLogout(configurationVersion: number): Promise<void> {
    if (!this.isCurrentConfigurationVersion(configurationVersion)) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.isLogoutInProgress = true;
    this.clearInactivityTimer();

    try {
      this.eventsService.publishLoggedOutEvent(new UserLoggedOutEvent());
    } catch (error) {
      console.error('Error during automatic logout by inactivity', error);
    }
  }

  private isCurrentConfigurationVersion(configurationVersion: number): boolean {
    return this.configurationVersion === configurationVersion;
  }
}
